import { BoxCardMenu } from './BoxCardMenu';
import { gameAccessTier } from '@src/components/PremiumBadge/tierIdentity';
import { DEFAULT_GAME_SEASON } from '@src/theme/season';
import './BoxesPage.css';
import { premiumGameUrl } from '@src/routing/boxDomainNavigation';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { BoxMode, isPlaceholderOrganizerUsername, type BoxInfo } from '@lub0v/santa-utils';
import {
  BoxIcon,
  BoxPicture,
  BoxShell,
  Button,
  Columns,
  Divider,
  Expandable,
  PlusIcon,
  Spinner,
  Stack,
  Typography,
} from '@src/components';

import { ACCOUNT_PATH, BOX_NEW_PATH, LOGIN_PATH } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppSelector } from '@src/state/hooks';

import { ThemedGameCard } from '@src/components/GameCard/ThemedGameCard';

function yearOf(box: BoxInfo): string {
  return String(box.box.year ?? '');
}

function roleLabel(box: BoxInfo, t: TFunction): string {
  const label = box.isAdmin
    ? t(box.isParticipant ? 'boxes.adminAndPlayer' : 'boxes.admin')
    : t('boxes.player');
  const organizerName = box.isAdmin ? box.admin.username?.trim() : undefined;
  const cardName = box.isParticipant
    ? box.cardUsername?.trim()
    : organizerName && !isPlaceholderOrganizerUsername(organizerName)
      ? organizerName
      : undefined;
  return cardName ? `${label} (${cardName})` : label;
}

function groupByYear(items: BoxInfo[]): [string, BoxInfo[]][] {
  const grouped = new Map<string, BoxInfo[]>();
  for (const item of items) {
    const year = yearOf(item);
    const list = grouped.get(year) ?? [];
    list.push(item);
    grouped.set(year, list);
  }
  return [...grouped.entries()].sort((a, b) => (a[0] > b[0] ? -1 : 1));
}

function BoxGrid({ items, t, onChanged }: { items: BoxInfo[]; t: TFunction; onChanged: (key: string, archived?: boolean) => void }) {
  const years = useMemo(() => groupByYear(items), [items]);

  return (
    <>
      {years.map(([year, yearItems]) => (
        <Stack key={year || 'year'} gap={5} wide>
          {year ? <Divider align="start">{year}</Divider> : null}
          <Columns columns={4} gap={4} className="boxes-page__grid">
            {yearItems.map((item) => {
              const meta = [
                t('boxes.participants', { count: item.participantsNumber }),
                roleLabel(item, t),
              ];
              if (item.isActiveDraw) {
                meta.push(t('boxes.drawn'));
              }
              return (
                <div className="boxes-page__card" key={item.box.key}>
                <ThemedGameCard
                  season={item.box.theme || DEFAULT_GAME_SEASON}
                  href={premiumGameUrl(item.box)}
                  picture={<BoxPicture picture={item.box.picture} logo={item.box.logo} />}
                  name={item.box.name}
                  meta={meta}
                  badge={item.box.mode === BoxMode.QUICK ? t('box.quick.badge') : undefined}
                  premiumTier={gameAccessTier(item.box.features)}
                  premiumLabel={item.box.isPremium ? t('newBox.appearance.premium') : undefined}
                  archived={item.box.isArchived}
                  testId={`boxes-card-${item.box.key}`}
                />
                <BoxCardMenu item={item} onChanged={onChanged} />
                </div>
              );
            })}
          </Columns>
        </Stack>
      ))}
    </>
  );
}

export function BoxesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.session.user);
  const sessionStatus = useAppSelector((state) => state.session.status);
  const [boxes, setBoxes] = useState<BoxInfo[] | null>(null);

  useEffect(() => {
    if (sessionStatus === 'idle' || sessionStatus === 'loading') {
      return;
    }
    if (!user) {
      navigate(LOGIN_PATH);
      return;
    }
    if (user.isGuest) {
      navigate(ACCOUNT_PATH);
    }
  }, [sessionStatus, user, navigate]);

  useEffect(() => {
    if (!user || user.isGuest) {
      return;
    }
    void santaClient.accountBoxes().then((res) => {
      if (res.ok && res.data) {
        setBoxes(res.data);
      } else {
        setBoxes([]);
      }
    });
  }, [user]);

  function onBoxChanged(key: string, archived?: boolean) {
    setBoxes(current => archived === undefined
      ? (current ?? []).filter(item => item.box.key !== key)
      : (current ?? []).map(item => item.box.key === key ? { ...item, box: { ...item.box, isArchived: archived } } : item));
  }

  const activeItems = useMemo(() => (boxes ?? []).filter((item) => !item.box.isArchived), [boxes]);
  const archivedItems = useMemo(() => (boxes ?? []).filter((item) => item.box.isArchived), [boxes]);
  if (sessionStatus === 'idle' || sessionStatus === 'loading' || !boxes) {
    return (
      <BoxShell testId="boxes-loading">
        <Stack gap={4} align="center">
          <Spinner size="medium" label={t('boxes.loading')} />
        </Stack>
      </BoxShell>
    );
  }

  return (
    <BoxShell testId="account-boxes">
      <Stack gap={10} wide className="landscape-sky-content">
        <header className="boxes-page__header">
          <Typography as="h1" role="title/page">{t('boxes.title')}</Typography>
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => navigate(BOX_NEW_PATH)}
            testId="boxes-create"
          >
            {t('boxes.create')}
          </Button>
        </header>

        <Stack gap={8} wide>
          {activeItems.length === 0 ? (
            <Stack gap={4} align="center" testId="boxes-empty">
              <Typography as="p" role="title/section">
                {t('boxes.empty')}
              </Typography>
              <Typography as="p" role="body/base">
                {t('boxes.emptyHint')}
              </Typography>
              <Button variant="primary" onClick={() => navigate(BOX_NEW_PATH)}>
                {t('boxes.create')}
              </Button>
            </Stack>
          ) : (
            <BoxGrid items={activeItems} t={t} onChanged={onBoxChanged} />
          )}
          {archivedItems.length > 0 ? (
            <Expandable
              className="expandable--mark-neutral"
              title={t('boxes.archivePanel', { count: archivedItems.length })}
              icon={<BoxIcon />}
              testId="boxes-archive-panel"
            >
              <Stack gap={8} wide>
                <Typography as="p" role="meta/helper">
                  {t('boxes.emptyArchiveHint')}
                </Typography>
                <BoxGrid items={archivedItems} t={t} onChanged={onBoxChanged} />
              </Stack>
            </Expandable>
          ) : null}
        </Stack>
      </Stack>
    </BoxShell>
  );
}
